export interface RetryOptions {
  /** Maximum number of retries after the initial attempt. */
  retries?: number;
  /** Delay before the first retry; doubles each subsequent retry. */
  baseDelayMs?: number;
  /** Upper bound for any single backoff delay. */
  maxDelayMs?: number;
}

/**
 * Runs `fn`, retrying on rejection with exponential backoff.
 *
 * Includes delays of 500ms, 1s, 2s — each capped at `maxDelayMs`
 * so the backoff never exceeds 5 seconds by default.
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  { retries = 1, baseDelayMs = 500, maxDelayMs = 5000 }: RetryOptions = {}
): Promise<T> {
  let attempt = 0;
  for (;;) {
    try {
      return await fn();
    } catch (err) {
      if (attempt >= retries) {
        throw err;
      }
      const delay = Math.min(baseDelayMs * 2 ** attempt, maxDelayMs);
      attempt += 1;
      await new Promise((resolve) => {
        setTimeout(resolve, delay);
      });
    }
  }
}
